import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Loader2, CreditCard, CheckCircle, XCircle,
  ExternalLink, RefreshCw, AlertTriangle, Copy
} from 'lucide-react';
import { toast } from 'sonner';

export const AdminStripeTest = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [toolsEnabled, setToolsEnabled] = useState(null); // null = unknown, true/false = known
  const [toolsStatus, setToolsStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [selectedTier, setSelectedTier] = useState('starter');
  const [tenantId, setTenantId] = useState('');
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [verifyingSession, setVerifyingSession] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);

  // Check for return from checkout
  const sessionIdFromUrl = searchParams.get('session_id');
  const isSuccess = searchParams.get('success') === 'true';
  const isCanceled = searchParams.get('canceled') === 'true';

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    checkToolsStatus();
  }, [navigate]);

  useEffect(() => {
    // Auto-verify if returning from checkout
    if (sessionIdFromUrl && isSuccess && toolsEnabled) {
      handleVerifySession(sessionIdFromUrl);
    }
  }, [sessionIdFromUrl, isSuccess, toolsEnabled]);

  const checkToolsStatus = async () => {
    try {
      const statusRes = await adminAPI.getStripeToolsStatus();
      setToolsEnabled(statusRes.data?.enabled || false);
      setToolsStatus(statusRes.data);
      
      if (statusRes.data?.enabled) {
        await fetchData();
      } else {
        setLoading(false);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        setToolsEnabled(false);
        setLoading(false);
      }
    }
  };

  const fetchData = async () => {
    try {
      const [configRes, sessionsRes] = await Promise.all([
        adminAPI.getStripeConfig(),
        adminAPI.getRecentStripeSessions()
      ]);
      setConfig(configRes.data);
      setRecentSessions(sessionsRes.data.transactions || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load Stripe config');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCheckout = async () => {
    setCreatingCheckout(true);
    setCheckoutResult(null);
    try {
      const res = await adminAPI.createTestCheckout({
        tier_id: selectedTier,
        tenant_id: tenantId || undefined
      });
      setCheckoutResult(res.data);
      toast.success('Checkout session created!');
      // Refresh recent sessions
      const sessionsRes = await adminAPI.getRecentStripeSessions();
      setRecentSessions(sessionsRes.data.transactions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create checkout');
      setCheckoutResult({ error: err.response?.data?.detail || 'Failed' });
    } finally {
      setCreatingCheckout(false);
    }
  };

  const handleVerifySession = async (sessionId) => {
    setVerifyingSession(sessionId);
    setVerificationResult(null);
    try {
      const res = await adminAPI.verifyStripeSession(sessionId);
      setVerificationResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify session');
      setVerificationResult({ error: err.response?.data?.detail || 'Failed' });
    } finally {
      setVerifyingSession(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show "Not available" page when tools are disabled
  if (toolsEnabled === false) {
    return (
      <div className="px-3 sm:px-6 lg:px-8 py-16">
        <Card className="max-w-lg mx-auto">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-bold mb-2">Not Available</h2>
            <p className="text-muted-foreground mb-4">
              Stripe Test Tools are currently disabled for production safety.
            </p>
            <code className="text-xs bg-muted px-3 py-2 rounded">
              Set ENABLE_STRIPE_TEST_TOOLS=true to enable
            </code>
            <Button 
              variant="outline" 
              className="mt-6"
              onClick={() => navigate('/admin/feedback')}
            >
              Return to Admin Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="px-3 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-center gap-2 mb-2">
          <h1 className="text-xl font-bold">Stripe Test Checkout</h1>
          <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
            Platform Admin Only
          </Badge>
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300 text-xs">
            Enabled via ENABLE_STRIPE_TEST_TOOLS
          </Badge>
        </div>
        {/* Return from Checkout Banner */}
        {sessionIdFromUrl && (
          <Card className={isSuccess ? 'border-green-500 bg-green-50' : 'border-yellow-500 bg-yellow-50'}>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                {isSuccess ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                )}
                <div>
                  <p className="font-medium">
                    {isSuccess ? 'Checkout Complete!' : isCanceled ? 'Checkout Canceled' : 'Returned from Checkout'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Session ID: {sessionIdFromUrl}
                  </p>
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="ml-auto"
                  onClick={() => handleVerifySession(sessionIdFromUrl)}
                  disabled={verifyingSession === sessionIdFromUrl}
                >
                  {verifyingSession === sessionIdFromUrl ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Verify Session'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stripe Config Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Stripe Configuration
            </CardTitle>
            <CardDescription>Current Stripe settings and checkout URL configuration</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase text-muted-foreground">Mode</Label>
                <div className="flex items-center gap-2">
                  <Badge variant={config?.is_test_mode ? 'secondary' : 'destructive'}>
                    {config?.mode?.toUpperCase() || 'Unknown'}
                  </Badge>
                  {config?.is_test_mode && (
                    <span className="text-sm text-green-600">✓ Safe for testing</span>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase text-muted-foreground">Webhook</Label>
                <div className="flex items-center gap-2">
                  {config?.webhook_configured ? (
                    <Badge variant="outline" className="text-green-600">Configured</Badge>
                  ) : (
                    <Badge variant="outline" className="text-yellow-600">Not Configured</Badge>
                  )}
                </div>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-xs uppercase text-muted-foreground">Success URL</Label>
                <code className="block text-xs bg-muted p-2 rounded break-all">
                  {config?.checkout_urls?.success_url}
                </code>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-xs uppercase text-muted-foreground">Cancel URL</Label>
                <code className="block text-xs bg-muted p-2 rounded break-all">
                  {config?.checkout_urls?.cancel_url}
                </code>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Create Test Checkout */}
        <Card>
          <CardHeader>
            <CardTitle>Create Test Checkout Session</CardTitle>
            <CardDescription>
              Create a real Stripe checkout session in test mode. Use test cards to complete payment.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pricing Tier</Label>
                <Select value={selectedTier} onValueChange={setSelectedTier}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.available_tiers?.map(tier => (
                      <SelectItem key={tier} value={tier}>
                        {tier.charAt(0).toUpperCase() + tier.slice(1)} - {config?.pricing?.[tier]?.price_display}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tenant ID (optional)</Label>
                <Input 
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  placeholder="Leave empty for first available tenant"
                />
              </div>
            </div>

            <Button 
              onClick={handleCreateCheckout} 
              disabled={creatingCheckout || !config?.is_test_mode}
              className="w-full md:w-auto"
            >
              {creatingCheckout ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Create Test Checkout
                </>
              )}
            </Button>

            {!config?.is_test_mode && (
              <p className="text-sm text-destructive">
                ⚠️ Test checkout is only available in Stripe test mode
              </p>
            )}
          </CardContent>
        </Card>

        {/* Checkout Result */}
        {checkoutResult && !checkoutResult.error && (
          <Card className="border-green-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-5 h-5" />
                Checkout Session Created
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Session ID</Label>
                  <div className="flex items-center gap-2">
                    <code className="text-xs">{checkoutResult.session_id}</code>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6"
                      onClick={() => copyToClipboard(checkoutResult.session_id)}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tenant</Label>
                  <p>{checkoutResult.tenant?.name}</p>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="font-medium mb-2">Test Card Numbers:</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Success:</span>{' '}
                    <code className="bg-muted px-2 py-1 rounded">{checkoutResult.test_cards?.success}</code>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Decline:</span>{' '}
                    <code className="bg-muted px-2 py-1 rounded">{checkoutResult.test_cards?.decline}</code>
                  </div>
                  <div>
                    <span className="text-muted-foreground">3D Secure:</span>{' '}
                    <code className="bg-muted px-2 py-1 rounded">{checkoutResult.test_cards?.requires_auth}</code>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Use any future expiry date and any 3-digit CVC
                </p>
              </div>

              <Button asChild className="w-full">
                <a href={checkoutResult.checkout_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Stripe Checkout
                </a>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Verification Result */}
        {verificationResult && !verificationResult.error && (
          <Card className="border-primary">
            <CardHeader>
              <CardTitle>Session Verification Result</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex flex-col items-center p-3 bg-muted rounded-lg">
                  {verificationResult.verification?.checkout_complete ? (
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  ) : (
                    <XCircle className="w-8 h-8 text-red-600" />
                  )}
                  <span className="text-xs mt-1">Checkout Complete</span>
                </div>
                <div className="flex flex-col items-center p-3 bg-muted rounded-lg">
                  {verificationResult.verification?.payment_received ? (
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  ) : (
                    <XCircle className="w-8 h-8 text-red-600" />
                  )}
                  <span className="text-xs mt-1">Payment Received</span>
                </div>
                <div className="flex flex-col items-center p-3 bg-muted rounded-lg">
                  {verificationResult.verification?.subscription_active ? (
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  ) : (
                    <XCircle className="w-8 h-8 text-yellow-600" />
                  )}
                  <span className="text-xs mt-1">Subscription Active</span>
                </div>
                <div className="flex flex-col items-center p-3 bg-muted rounded-lg">
                  {verificationResult.verification?.urls_valid ? (
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  ) : (
                    <XCircle className="w-8 h-8 text-red-600" />
                  )}
                  <span className="text-xs mt-1">URLs Valid</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Session Status</Label>
                  <Badge>{verificationResult.session?.status}</Badge>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Payment Status</Label>
                  <Badge variant={verificationResult.session?.payment_status === 'paid' ? 'default' : 'secondary'}>
                    {verificationResult.session?.payment_status}
                  </Badge>
                </div>
                {verificationResult.subscription && (
                  <>
                    <div>
                      <Label className="text-xs uppercase text-muted-foreground">Subscription ID</Label>
                      <code className="text-xs">{verificationResult.subscription?.id}</code>
                    </div>
                    <div>
                      <Label className="text-xs uppercase text-muted-foreground">Subscription Status</Label>
                      <Badge variant={verificationResult.subscription?.status === 'active' ? 'default' : 'secondary'}>
                        {verificationResult.subscription?.status}
                      </Badge>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Sessions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent Checkout Sessions</CardTitle>
              <CardDescription>Recent test checkout attempts from database</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {recentSessions.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No recent sessions</p>
            ) : (
              <div className="space-y-2">
                {recentSessions.map((session) => (
                  <div 
                    key={session.id}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg text-sm"
                  >
                    <div className="space-y-1">
                      <code className="text-xs">{session.stripe_session_id}</code>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          ${(session.amount_cents / 100).toFixed(2)}
                        </Badge>
                        <Badge 
                          variant={session.status === 'completed' ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {session.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(session.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleVerifySession(session.stripe_session_id)}
                      disabled={verifyingSession === session.stripe_session_id}
                    >
                      {verifyingSession === session.stripe_session_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
